/**
 * @ipc/admin-ui · app/routes/route-manifest — РЕЕСТР экранов панели.
 *
 * КОНТРАКТ для фазы 2: каждый экран-агент добавляет сюда ОДНУ запись RouteEntry
 * и (при желании пункта меню) ставит `nav`. Роутер (app/router.tsx) и навбар
 * (widgets/app-shell) строятся из этого массива — экран не трогает ни то, ни другое.
 *
 * Поля:
 *  - path        — путь react-router (уникальный).
 *  - permission  — код права RBAC для доступа/видимости (null = доступно всем авторизованным).
 *  - lazy        — динамический импорт модуля страницы; модуль экспортирует `Page` (React-компонент).
 *  - nav         — если задан, пункт появляется в меню навбара (label + lucide-иконка).
 *  - index       — true для корневого дэшборда (path '/').
 *
 * Регистрация нового экрана (фаза 2):
 *  1) создать pages/<screen>/ui/<Screen>Page.tsx с `export function Page()` и барель index.ts (export { Page });
 *  2) заменить lazy-заглушку на `() => import('@pages/<screen>')`;
 *  3) при необходимости пункта меню — заполнить `nav: { label, icon }`.
 */
import type { ComponentType } from 'react';
import {
  LayoutDashboard,
  CreditCard,
  Link2,
  BarChart3,
  ScrollText,
  ShieldCheck,
  Webhook,
  Zap,
  Store,
  KeyRound,
  Key,
  Users,
  Building2,
  type LucideIcon,
} from 'lucide-react';

/** Модуль страницы: экспортирует React-компонент `Page`. */
export interface PageModule {
  Page: ComponentType;
}

export interface NavMeta {
  label: string;
  icon: LucideIcon;
}

export interface RouteEntry {
  path: string;
  permission: string | null;
  lazy: () => Promise<PageModule>;
  nav?: NavMeta;
  index?: boolean;
}

/**
 * РЕЕСТР. Порядок = порядок пунктов меню. Детальные роуты (без nav) идут рядом
 * со своим списком. Login объявлен отдельно в router.tsx (вне shell).
 */
export const routeManifest: RouteEntry[] = [
  {
    path: '/',
    index: true,
    permission: null,
    lazy: () => import('@pages/dashboard'),
    nav: { label: 'Дэшборд', icon: LayoutDashboard },
  },
  {
    path: '/payments',
    permission: 'payments.read',
    lazy: () => import('@pages/payments'),
    nav: { label: 'Платежи', icon: CreditCard },
  },
  {
    path: '/payments/:uuid',
    permission: 'payments.read',
    lazy: () => import('@pages/payment-detail'),
  },
  {
    path: '/links',
    permission: 'links.read',
    lazy: () => import('@pages/links'),
    nav: { label: 'Ссылки', icon: Link2 },
  },
  {
    path: '/links/new',
    permission: 'links.create',
    lazy: () => import('@pages/link-create'),
  },
  {
    path: '/links/:uuid',
    permission: 'links.read',
    lazy: () => import('@pages/links/ui/link-detail-page'),
  },
  {
    path: '/analytics',
    permission: 'analytics.read',
    lazy: () => import('@pages/analytics'),
    nav: { label: 'Аналитика', icon: BarChart3 },
  },
  {
    path: '/logs',
    permission: 'logs.read',
    lazy: () => import('@pages/logs'),
    nav: { label: 'Логи', icon: ScrollText },
  },
  {
    path: '/audit',
    permission: 'logs.read',
    lazy: () => import('@pages/audit'),
    nav: { label: 'Аудит', icon: ShieldCheck },
  },
  {
    path: '/webhooks',
    permission: 'webhooks.read',
    lazy: () => import('@pages/webhooks'),
    nav: { label: 'Вебхуки', icon: Webhook },
  },
  {
    path: '/actions',
    permission: 'webhooks.read',
    lazy: () => import('@pages/actions'),
    nav: { label: 'Действия', icon: Zap },
  },
  {
    path: '/merchants',
    permission: 'merchants.read',
    lazy: () => import('@pages/merchants'),
    nav: { label: 'Мерчанты', icon: Store },
  },
  {
    path: '/roles',
    permission: 'roles.manage',
    lazy: () => import('@pages/roles'),
    nav: { label: 'Роли', icon: KeyRound },
  },
  {
    path: '/api-keys',
    permission: 'roles.manage',
    lazy: () => import('@pages/api-keys'),
    nav: { label: 'API-ключи', icon: Key },
  },
  {
    path: '/users',
    permission: 'roles.manage',
    lazy: () => import('@pages/users'),
    nav: { label: 'Пользователи', icon: Users },
  },
  {
    path: '/tenants',
    permission: 'tenants.read',
    lazy: () => import('@pages/tenants'),
    nav: { label: 'Тенанты', icon: Building2 },
  },
  {
    path: '/tenants/:slug',
    permission: 'tenants.read',
    lazy: () => import('@pages/tenants/ui/tenant-detail-page'),
  },
];
